import type { Monster, Move, Rarity } from './types'
import { hashSeed, mulberry32, rollMaxEvolutions } from './generator'

// ---------------------------------------------------------------------------
// XP par monstre — local (localStorage), pour les monstres démo ET on-chain.
// Le niveau scale les stats de façon symétrique (l'adversaire est généré au
// même niveau), donc c'est de la progression, pas un avantage: l'équilibrage
// des familles reste celui validé par simulation au niveau 1.
// ---------------------------------------------------------------------------

const KEY_XP = 'cryptoket.xp'

export const MAX_LEVEL = 100
export const XP_WIN = 25
export const XP_LOSS = 10

// L'XP gagnée n'est PAS fixe: elle dépend de la rareté ET du niveau du monstre
// VAINCU — battre un légendaire haut niveau rapporte gros, un commun bas niveau
// peu (les défaites rapportent un peu d'XP, également modulée par l'adversaire).
export const XP_RARITY_MULT: Record<Rarity, number> = {
  common: 1.0,
  rare: 1.4,
  epic: 1.9,
  legendary: 2.6,
}

/** Combo de série: chaque victoire consécutive dope l'XP de +10%, plafonné ×2
 * (série de 11+). La série se lit APRÈS enregistrement de la victoire. */
export const COMBO_STEP = 0.1
export const COMBO_CAP = 2
export function comboMultiplier(streak: number): number {
  if (streak <= 1) return 1
  return Math.min(COMBO_CAP, 1 + (streak - 1) * COMBO_STEP)
}

export function xpForResult(won: boolean, opponentRarity: Rarity, opponentLevel = 1): number {
  const base = won ? XP_WIN : XP_LOSS
  // Facteur de niveau: +4% par niveau de l'adversaire (Nv50 ≈ ×3, Nv100 ≈ ×5).
  const levelFactor = 1 + (Math.max(1, opponentLevel) - 1) * 0.04
  return Math.round(base * XP_RARITY_MULT[opponentRarity] * levelFactor)
}

// XP cumulé requis par niveau, jusqu'à 100. Incrément ACCÉLÉRANT (le pas grandit
// lui-même) → chaque niveau coûte de plus en plus, donc de plus en plus de
// combats vers le Nv100. 2→40, 3→92, 4→156, …
const THRESHOLDS: number[] = (() => {
  const t = [0, 0]
  let need = 40
  let step = 12
  for (let lvl = 2; lvl <= MAX_LEVEL; lvl++) {
    t.push(t[lvl - 1] + need)
    need += step
    step += 2
  }
  return t
})()

export function levelFromXp(xp: number): number {
  let level = 1
  while (level < MAX_LEVEL && xp >= THRESHOLDS[level + 1]) level++
  return level
}

/** Progression vers le prochain niveau, pour les barres d'XP. */
export function xpProgress(xp: number): { level: number; pct: number; toNext: number | null } {
  const level = levelFromXp(xp)
  if (level >= MAX_LEVEL) return { level, pct: 100, toNext: null }
  const floor = THRESHOLDS[level]
  const ceil = THRESHOLDS[level + 1]
  return { level, pct: Math.round(((xp - floor) / (ceil - floor)) * 100), toNext: ceil - xp }
}

export function levelMultiplier(level: number): number {
  return 1 + 0.02 * (Math.min(level, MAX_LEVEL) - 1)
}

function loadXpMap(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(KEY_XP) ?? '{}') as Record<string, number>
  } catch {
    return {}
  }
}

export function xpOf(monsterId: string): number {
  return loadXpMap()[monsterId] ?? 0
}

export function addXp(
  monsterId: string,
  amount: number,
): { xp: number; level: number; leveledUp: boolean } {
  const map = loadXpMap()
  const before = levelFromXp(map[monsterId] ?? 0)
  const xp = (map[monsterId] ?? 0) + amount
  map[monsterId] = xp
  localStorage.setItem(KEY_XP, JSON.stringify(map))
  const level = levelFromXp(xp)
  return { xp, level, leveledUp: level > before }
}

const EVOLVED_MARK = ' ⁺'

/**
 * Les métamorphoses affûtent AUSSI les coups (symétrique: les adversaires
 * générés au même niveau les reçoivent aussi; l'arène classée reste aux coups
 * de base — elle régénère depuis la chaîne):
 *   ⬆ Évolué : chaque spéciale s'affûte — coût -15%
 *   ★ Apex  : chaque attaque normale gagne +6 de puissance (coup signature)
 * Généralisé aux movesets de 1 à 4 coups. Marqueur ⁺ = anti double-application.
 */
function evolveMoves(moves: Move[], stage: Stage): Move[] {
  if (stage === 0) return moves
  const adorned = (name: string) => (name.endsWith(EVOLVED_MARK) ? name : name + EVOLVED_MARK)
  return moves.map((mv) => {
    if (mv.name.endsWith(EVOLVED_MARK)) return mv
    if (mv.kind === 'special') {
      return { ...mv, name: adorned(mv.name), cost: Math.max(15, Math.round(mv.cost * 0.85)) }
    }
    if (stage >= 2) return { ...mv, name: adorned(mv.name), power: mv.power + 6 }
    return mv
  })
}

/**
 * Nombre de métamorphoses possibles d'un monstre — la variable posée au mint.
 * Prend `monster.maxEvolutions` si présent, sinon la re-dérive du seed (compat
 * des monstres créés avant l'ajout du champ, et des NFTs on-chain famille+seed).
 */
export function evolutionsOf(monster: Pick<Monster, 'seed' | 'rarity' | 'maxEvolutions'>): 0 | 1 | 2 {
  if (monster.maxEvolutions !== undefined) return monster.maxEvolutions
  const rng = mulberry32(hashSeed(monster.seed))
  rng() // 1er draw = rareté (déjà connue) — on l'avance pour aligner la séquence
  return rollMaxEvolutions(rng, monster.rarity)
}

// Bonus de stats à CHAQUE métamorphose — l'évolution rend plus fort (au-delà du
// simple gain de niveau). Cumulatif par stade.
export const EVO_STAT_MULT: Record<Stage, number> = { 0: 1.0, 1: 1.1, 2: 1.22 }

/** Attache niveau + XP au monstre, scale ses stats (+2%/niveau, + bonus d'évolution) et fait évoluer ses coups. */
export function applyLevel(monster: Monster, levelOverride?: number): Monster {
  const xp = levelOverride === undefined ? xpOf(monster.id) : 0
  const level = levelOverride ?? levelFromXp(xp)
  const stage = stageOf(level, evolutionsOf(monster))
  const k = levelMultiplier(level) * EVO_STAT_MULT[stage]
  const scale = (v: number) => Math.round(v * k)
  return {
    ...monster,
    level,
    xp,
    maxEvolutions: evolutionsOf(monster),
    moves: evolveMoves(monster.moves, stage),
    stats:
      level <= 1
        ? monster.stats
        : {
            hp: scale(monster.stats.hp),
            attack: scale(monster.stats.attack),
            defense: scale(monster.stats.defense),
            speed: scale(monster.stats.speed),
            energy: scale(monster.stats.energy),
          },
  }
}

// ---------------------------------------------------------------------------
// Métamorphoses — le monstre change de forme à des paliers de niveau.
// Stade dérivé du niveau (donc du seed + de l'XP): déterministe, visible dans
// les replays (le niveau y est encodé), et chaque transformation est
// enregistrée dans un journal local.
// ---------------------------------------------------------------------------

export type Stage = 0 | 1 | 2

export const STAGE_INFO: Record<Stage, { name: string; badge: string; minLevel: number; perk: string }> = {
  0: { name: 'Base', badge: '', minLevel: 1, perk: '' },
  1: { name: 'Évolué', badge: '⬆', minLevel: 20, perk: 'spéciales affûtées : coût -15% ⚡' },
  2: { name: 'Apex', badge: '★', minLevel: 50, perk: 'coups signature : attaques +6 puissance' },
}

/**
 * Stade d'évolution atteint. Dérivé du niveau MAIS plafonné par le nombre de
 * métamorphoses tiré au mint (`maxEvolutions`): un monstre à 0 évolution garde
 * sa forme de base même au Nv100 (il grandit en stats, pas en forme).
 */
export function stageOf(level: number, maxEvolutions: 0 | 1 | 2 = 2): Stage {
  const byLevel: Stage = level >= STAGE_INFO[2].minLevel ? 2 : level >= STAGE_INFO[1].minLevel ? 1 : 0
  return Math.min(byLevel, maxEvolutions) as Stage
}

const KEY_EVO = 'cryptoket.evolutions'

interface Metamorphosis {
  stage: Stage
  at: number
}

function loadEvolutions(): Record<string, Metamorphosis[]> {
  try {
    return JSON.parse(localStorage.getItem(KEY_EVO) ?? '{}') as Record<string, Metamorphosis[]>
  } catch {
    return {}
  }
}

/** Journal des métamorphoses d'un monstre (stade + date). */
export function metamorphosesOf(monsterId: string): Metamorphosis[] {
  return loadEvolutions()[monsterId] ?? []
}

export function recordMetamorphosis(monsterId: string, stage: Stage): void {
  const all = loadEvolutions()
  const list = all[monsterId] ?? []
  if (list.some((m) => m.stage === stage)) return
  list.push({ stage, at: Date.now() })
  all[monsterId] = list
  localStorage.setItem(KEY_EVO, JSON.stringify(all))
}

// ---------------------------------------------------------------------------
// Rang du joueur — la hiérarchie des holders crypto.
// Victoire: +20 pts (+5 par victoire de la série en cours, plafonné à +20).
// Défaite: -10 pts (plancher 0) et la série retombe à zéro.
// ---------------------------------------------------------------------------

export interface Rank {
  name: string
  emoji: string
  min: number
}

export const RANKS: Rank[] = [
  { name: 'Crevette', emoji: '🦐', min: 0 },
  { name: 'Crabe', emoji: '🦀', min: 100 },
  { name: 'Poisson', emoji: '🐟', min: 250 },
  { name: 'Dauphin', emoji: '🐬', min: 500 },
  { name: 'Requin', emoji: '🦈', min: 900 },
  { name: 'Baleine', emoji: '🐋', min: 1500 },
]

export function rankFromPoints(points: number): Rank & { next: Rank | null; pct: number } {
  let idx = 0
  for (let i = RANKS.length - 1; i >= 0; i--) {
    if (points >= RANKS[i].min) {
      idx = i
      break
    }
  }
  const rank = RANKS[idx]
  const next = RANKS[idx + 1] ?? null
  const pct = next ? Math.round(((points - rank.min) / (next.min - rank.min)) * 100) : 100
  return { ...rank, next, pct }
}

export function pointsForWin(currentStreak: number): number {
  return 20 + Math.min(currentStreak * 5, 20)
}

export const POINTS_LOSS = -10
